import {convert} from 'html-to-text';
import TurndownService from 'turndown';

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '•'
});

export const exportRFPComparison = async (content, format, formData = {}) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const baseFilename = `RFP-Comparison-Analysis-${timestamp}`;

  switch (format) {
    case 'html':
      return {
        content: convertToHTML(content, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };
    case 'markdown':
      return {
        content: content, // Already in markdown-like format
        filename: `${baseFilename}.md`,
        mimeType: 'text/markdown'
      };
    case 'text':
      return {
        content: convertToPlainText(content),
        filename: `${baseFilename}.txt`,
        mimeType: 'text/plain'
      };
    case 'docx':
      return {
        content: convertToWordFormat(content, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

const convertToHTML = (content, formData) => {
  if (!content) return '';
  
  // Convert markdown-like content to HTML
  let html = content
    // Convert headers FIRST before any other processing
    .replace(/^# (.+)$/gm, '<h1 class="main-heading">$1</h1>')
    .replace(/^## (.+)$/gm, '<h2 class="section-heading">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="sub-heading">$1</h3>')
    // Convert bold text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Convert tables (basic markdown table support)
    .replace(/\|(.+)\|/g, (match, content) => {
      const cells = content.split('|').map(cell => cell.trim());
      const cellsHtml = cells.map(cell => `<td>${cell}</td>`).join('');
      return `<tr>${cellsHtml}</tr>`;
    })
    // Convert bullet points
    .replace(/^• (.+)$/gm, '<li class="bullet-item">$1</li>')
    .replace(/^- (.+)$/gm, '<li class="bullet-item">$1</li>')
    // Convert line breaks to paragraphs (but preserve headers and lists)
    .split('\n\n')
    .map(paragraph => {
      const trimmed = paragraph.trim();
      if (!trimmed) return '';
      
      // Don't wrap headers, lists, or tables in paragraphs
      if (trimmed.includes('<h1') || trimmed.includes('<h2') || trimmed.includes('<h3') ||
          trimmed.includes('<li') || trimmed.includes('<tr>') || trimmed.includes('<table')) {
        return trimmed;
      }
      
      // Convert single line breaks to <br> within paragraphs
      const withBreaks = trimmed.replace(/\n/g, '<br>');
      return `<p class="paragraph">${withBreaks}</p>`;
    })
    .join('\n\n');

  // Wrap consecutive list items in ul tags
  html = html.replace(/(<li class="bullet-item">.*?<\/li>(\s*\n\s*<li class="bullet-item">.*?<\/li>)*)/gs, (match) => {
    return `<ul class="bullet-list">${match}</ul>`;
  });

  // Wrap table rows in table structure
  html = html.replace(/(<tr>.*?<\/tr>(\s*\n\s*<tr>.*?<\/tr>)*)/gs, (match) => {
    const rows = match.match(/<tr>.*?<\/tr>/g);
    if (rows && rows.length > 0) {
      const headerRow = rows[0];
      const bodyRows = rows.slice(1);
      return `<table class="comparison-table">
        <thead>${headerRow}</thead>
        <tbody>${bodyRows.join('\n')}</tbody>
      </table>`;
    }
    return match;
  });

  const analysisDate = new Date().toLocaleDateString();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>RFP Comparison Analysis - ${analysisDate}</title>
  <style>
    body {
      font-family: 'Arial', sans-serif;
      line-height: 1.6;
      max-width: 1000px;
      margin: 0 auto;
      padding: 40px 20px;
      color: #333;
    }
    
    .header {
      text-align: center;
      border-bottom: 3px solid #6366f1;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    
    .header h1 {
      color: #4338ca;
      margin-bottom: 10px;
      font-size: 28px;
    }
    
    .meta-info {
      background: #f8fafc;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
      border-left: 4px solid #6366f1;
    }
    
    .main-heading {
      color: #4338ca !important;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 10px;
      font-size: 24px !important;
      margin: 30px 0 20px 0 !important;
    }
    
    .section-heading {
      color: #5b21b6 !important;
      margin-top: 30px !important;
      margin-bottom: 15px !important;
      font-size: 20px !important;
    }
    
    .sub-heading {
      color: #6366f1 !important;
      margin-top: 20px !important;
      margin-bottom: 10px !important;
      font-size: 16px !important;
    }
    
    .comparison-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      background: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    
    .comparison-table th, .comparison-table td {
      border: 1px solid #d1d5db;
      padding: 12px;
      text-align: left;
      vertical-align: top;
    }
    
    .comparison-table thead th {
      background: #6366f1;
      color: white;
      font-weight: bold;
    }
    
    .comparison-table tbody tr:nth-child(even) {
      background: #f9fafb;
    }
    
    .bullet-list {
      margin: 15px 0;
      padding-left: 25px;
    }
    
    .bullet-item {
      margin: 8px 0;
      line-height: 1.5;
    }
    
    .paragraph {
      margin: 15px 0;
      text-align: justify;
    }
    
    strong {
      color: #374151;
      font-weight: 600;
    }
    
    .highlight {
      background: #fef3c7;
      padding: 2px 4px;
      border-radius: 3px;
    }
    
    @media print {
      body {
        padding: 20px;
      }
      .comparison-table {
        break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>RFP Comparison Analysis</h1>
    <p>City of Metropolis Procurement Analysis</p>
  </div>
  
  <div class="meta-info">
    <strong>Analysis Date:</strong> ${analysisDate}<br>
    <strong>Generated:</strong> ${new Date().toLocaleString()}<br>
    ${formData?.analysisType ? `<strong>Analysis Type:</strong> ${formData.analysisType}<br>` : ''}
    ${formData?.focusAreas ? `<strong>Focus Areas:</strong> ${formData.focusAreas.join(', ')}` : ''}
  </div>
  
  <div class="content">
    ${html}
  </div>
  
  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
    Generated by City of Metropolis AI Procurement Analysis System
  </div>
</body>
</html>`;
};

const convertToPlainText = (content) => {
  // Remove markdown formatting for plain text
  return content
    .replace(/^#{1,6} /gm, '') // Remove header markers
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers
    .replace(/^• /gm, '- ') // Convert bullet points
    .replace(/\|/g, ' | ') // Format tables
    .trim();
};

const convertToWordFormat = (content, formData) => {
  // For Word format, we'll generate HTML that can be easily converted
  return convertToHTML(content, formData);
};

export default {
  exportRFPComparison
};